import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { ConfirmationService } from "primeng/api";
import { catchError, of, take } from "rxjs";
import { Subject } from "rxjs/internal/Subject";
import { AdminRestService } from '../../services/rest/admin-rest.service';
import { PremiseHazardCommandsRestService } from "@services/rest/premise-hazard-command-rest.service";
import { PremiseHazardQueryRestService } from "@services/rest/premise-hazard-rest.service";
import { UserService } from "@services/user.service";
import { Address } from "@address-models/address.model";
import { GetAddressesRequestModel } from "@address-models/get-addresses-request.model";
import { PremiseHazardType } from "@premise-hazard-type-models/premise-hazard-type.model";
import { GetPremiseHazardTypesRequestModel } from "@premise-hazard-type-models/get-premise-hazard-type-request.model";
import { PremiseHazard } from "@premise-hazard-models/premise-hazard.model";
import { GetPremiseHazardRequestModel } from "@premise-hazard-models/get-premise-hazard-request.model";
import { IncludeAttachmentType } from "@premise-hazard-models/include-attachment-type.model";
import { ResponseItem } from "@models/response-item.model";
import { BasePageComponent } from "../../shared/base-page.component";
import { PremiseHazardReportRequestModel } from "@app/models/premise-hazard/request/premise-hazard-report-request.model";
import { QuickEditEvent } from "@app/models/quick-edit-event.model";

@Component({
  selector: 'app-premise-hazard-page',
  templateUrl: './premise-hazard-page.component.html',
  styleUrls: ['./premise-hazard-page.component.scss'],
  standalone: false,
})

export default class PremiseHazardsPageComponent extends BasePageComponent<PremiseHazard> implements OnInit, OnDestroy, AfterViewInit {

  constructor(
    private readonly queryService: PremiseHazardQueryRestService,
    private readonly commandService: PremiseHazardCommandsRestService,
    private readonly transloco: TranslocoService,
    adminApi: AdminRestService,
    confirmationService: ConfirmationService,
    cdr: ChangeDetectorRef,
    userService: UserService) {
    super(adminApi, confirmationService, cdr, userService);
  }

  public HasAdminPermissions: boolean = false;

  public openDeleteConfirmDialog(): void {
    const header = this.transloco.translate('PremiseHazard.Page.modal.delete.header');
    const message = this.transloco.translate('PremiseHazard.Page.modal.delete.content', { premiseHazardName: this.SelectedPremiseHazard!.recordTitle });
    this.openDeleteConfirm(header, message);
  }

  // Expose base class properties with specific names for template compatibility
  get SelectedPremiseHazard(): PremiseHazard | undefined {
    return this.SelectedItem;
  }
  set SelectedPremiseHazard(value: PremiseHazard | undefined) {
    this.SelectedItem = value;
  }

  get SelectedPremiseHazardEtag(): string | undefined {
    return this.SelectedItemEtag;
  }
  set SelectedPremiseHazardEtag(value: string | undefined) {
    this.SelectedItemEtag = value;
  }

  get isSharedHazard(): boolean {
    return this.isShared;
  }
  set isSharedHazard(value: boolean) {
    this.isShared = value;
  }

  get changePremiseHazardViewSubject(): Subject<ResponseItem<PremiseHazard> | undefined> {
    return this.changeViewSubject as Subject<ResponseItem<PremiseHazard> | undefined>;
  }

  get premiseHazardDeletedSubject(): Subject<PremiseHazard> {
    return this.itemDeletedSubject as Subject<PremiseHazard>;
  }

  get canCreateReports(): boolean {
    return this.AllowInteraction && !!this.SelectedPremiseHazard && this.HasAdminPermissions;
  }

  PremiseHazardTypes: PremiseHazardType[] = new Array<PremiseHazardType>();
  Addresses: Address[] = new Array<Address>();
  public IsQuickEdit: boolean = false;

  private typeContinuationToken?: string;
  private addressContinuationToken?: string;
  private typesLoading: boolean = true;
  private addressesLoading: boolean = true;

  EditButtonTooltipText: string = '';
  DeleteButtonTooltipText: string = '';

  public override ngOnInit(): void {
    super.ngOnInit();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    this.HasAdminPermissions = this.userService.hasAdminPermissions();
    this.loadPremiseHazardTypes();
    this.loadAddresses();
  }

  onPremiseHazardSelected(premiseHazard: PremiseHazard): void {
    this.selectItem(premiseHazard);
  }

  onDeletePremiseHazardClicked(premiseHazard: PremiseHazard): void {
    this.SelectedPremiseHazard = premiseHazard;
    this.openDeleteConfirmDialog();
  }

  onQuickEditPremiseHazardClicked(premiseHazard: PremiseHazard): void {
    this.SelectedPremiseHazard = premiseHazard;
    this.IsQuickEdit = true;
    this.IsCreating = false;
    this.listHidden = true;
    this.IsViewing = false;
    this.IsEditing = true;
    this.interactionDisabled();
    this.loadItemForEdit(premiseHazard.id);
  }

  public onQuickEditEvent(event: QuickEditEvent): void {
    if (!event.itemCreated) {
      this.IsEditing = false;
      this.IsViewing = false;
      this.listHidden = false;
      this.SelectedPremiseHazard = undefined;
      this.SelectedPremiseHazardEtag = undefined;
      this.IsQuickEdit = false;
      return;
    }
    this.queryService.getPremiseHazard(event.itemId!, {
      includeAttachments: IncludeAttachmentType.none,
      includeExtensionData: false,
      includeSupportingDocuments: true,
    })
      .pipe(take(1))
      .subscribe(response => {
        this.SelectedPremiseHazard = response.Item;
        this.SelectedPremiseHazardEtag = response.ETag;
        this.changePremiseHazardViewSubject.next(response);
        this.IsViewing = true;
        this.IsEditing = false;
        this.listHidden = true;
        this.IsQuickEdit = false;
      });
  }

  loadPremiseHazardTypes(): void {
    this.typesLoading = true;
    let request: GetPremiseHazardTypesRequestModel = {
      continuationToken: this.typeContinuationToken,
      includeSharedData: false,
      maxItemCount: 100,
    };
    this.queryService.getPremiseHazardTypes(request)
      .pipe(take(1))
      .subscribe(response => {
        this.PremiseHazardTypes = response.premiseHazardTypes;
        this.typeContinuationToken = response.continuationToken;
        if (this.typeContinuationToken) {
          this.loadPremiseHazardTypes();
        } else {
          this.typesLoading = false;
          this.setInteractionState();
        }
      });
  }

  loadAddresses(): void {
    this.addressesLoading = true;
    let request: GetAddressesRequestModel = {
      continuationToken: this.addressContinuationToken,
      includeSharedData: false,
      listOnly: true,
      maxItemCount: 100,
      returnExtensionData: false,
    };
    this.queryService.getAddresses(request)
      .pipe(take(1))
      .subscribe(response => {
        this.Addresses.push(...response.addresses);
        this.addressContinuationToken = response.continuationToken;
        if (this.addressContinuationToken) {
          this.loadAddresses();
        } else {
          this.addressesLoading = false;
          this.setInteractionState();
        }
      });
  }

  public onHazardReloaded(response: ResponseItem<PremiseHazard>): void {
    this.SelectedPremiseHazard = response.Item;
    this.SelectedPremiseHazardEtag = response.ETag;
    this.changePremiseHazardViewSubject.next(response);
  }

  public onHazardNotFound(premiseHazard: PremiseHazard): void {
    this.premiseHazardDeletedSubject.next(premiseHazard);
    this.showList();
  }

  public refreshHazard(): void {
    if (!this.SelectedPremiseHazard) {
      return;
    }

    this.queryService.getPremiseHazard(this.SelectedPremiseHazard.id, {
      includeAttachments: IncludeAttachmentType.none,
      includeExtensionData: false,
      includeSupportingDocuments: true,
    })
      .pipe(take(1))
      .subscribe(response => {
        this.SelectedPremiseHazard = response.Item;
        this.AllowInteraction = false;
        this.changePremiseHazardViewSubject.next(response);
      });
  }

  public addNewPremiseHazardClicked(): void {
    this.addNewItemClicked();
  }

  public setSharedHazard(event: any): void {
    this.setShared(event);
  }

  // Override base class methods
  public override setInteractionState(): void {
    this.AllowInteraction = !(this.typesLoading || this.addressesLoading);
  }

  protected override onRefreshClicked(): void {
    this.typeContinuationToken = undefined;
    this.addressContinuationToken = undefined;
    this.loadPremiseHazardTypes();
    this.loadAddresses();
  }

  protected override deleteItem(): void {
    if (!this.SelectedPremiseHazard) {
      return;
    }
    this.interactionDisabled();
    const requestModel: GetPremiseHazardRequestModel = {
      includeSupportingDocuments: true,
      includeExtensionData: true,
      includeAttachments: IncludeAttachmentType.none,
    };
    this.queryService.getPremiseHazard(this.SelectedPremiseHazard.id, requestModel)
      .pipe(take(1))
      .subscribe(response => {
        if (response.Item) {
          const id = response.Item.id;
          const etag = response.ETag;
          this.IsViewing = true;
          this.IsEditing = false;
          this.commandService.deletePremiseHazard(id, etag!)
            .pipe(take(1))
            .subscribe(resp => {
              this.premiseHazardDeletedSubject.next(response.Item!);
              this.listHidden = false;
              this.interactionEnabled();
            });
        }
      });
  }

  protected override loadItemForEdit(id: string): void {
    const requestModel: GetPremiseHazardRequestModel = {
      includeSupportingDocuments: true,
      includeExtensionData: true,
      includeAttachments: IncludeAttachmentType.none,
    };
    this.queryService.getPremiseHazard(id, requestModel)
      .pipe(take(1))
      .subscribe(response => {
        if (response.Item) {
          this.SelectedPremiseHazard = response.Item;
          this.SelectedPremiseHazardEtag = response.ETag;
          this.IsViewing = false;
          this.IsEditing = true;
          this.interactionEnabled();
        }
      });
  }

  createPremiseHazardReport() {
    if (!this.SelectedPremiseHazard) {
      return;
    }
    this.interactionDisabled();
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const cultureInfo = this.transloco.getActiveLang();
    const requestModel: PremiseHazardReportRequestModel = {
      premiseHazardIds: [this.SelectedPremiseHazard.id],
      timeZoneId: timeZoneId,
      cultureInfo: cultureInfo,
    };
    this.adminApi.createHazardReport(requestModel)
        .pipe(take(1), catchError(() => {
          this.interactionEnabled();
          return of();
        }))
        .subscribe(response => {
          const timestamp = Date.now();
          const date = new Date(timestamp);
          const year = date.getFullYear();
          const month = new String(date.getMonth() + 1).padStart(2, '0');
          const day = new String(date.getDate()).padStart(2, '0');
          let filename = `premise-hazard-reports-${year}-${month}-${day}.zip`;
          const downloadUrl = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          window.open(downloadUrl, '_blank');
          this.interactionEnabled();
        });
  }
}
